/**
 * O QUE A TELA DE PESSOAS PEDE, E COM QUE LICENÇA.
 *
 * Achado dos robôs no QAS (20/10/2026): operador, oficina, controladoria,
 * almoxarifado, compras e planejador abriam `/pessoas` e a tela disparava cinco
 * ou seis consultas que o perfil não lê — `/pessoas/funcoes`, `exigencias`,
 * `documentos`, `tipos-de-documento`, `aptidoes` —, todas 403, e um toast
 * "Não foi feito… sem permissão" sobre uma tela que a pessoa nem pediu para
 * mudar. A mesma família do defeito da ficha do operador (36a8529).
 *
 * A regra: cada consulta só sai com a licença da rota que ela chama (o
 * `config.exige` do backend), e o que o perfil não lê aparece no estado "sem
 * permissão" desenhado (p.34), nunca num toast. A rota e a licença moram aqui
 * juntas, e o teste `leitura.test.ts` confere as duas contra
 * `backend/src/rotas/pessoas.ts`: a licença daqui não pode divergir da do
 * servidor.
 */

export const CONSULTAS_DE_PESSOAS = {
  impedimentos: { rota: '/pessoas/impedimentos', licenca: 'impedimento.ler' },
  documentos: { rota: '/pessoas/documentos', licenca: 'documento_de_pessoa.ler' },
  tipos: { rota: '/pessoas/tipos-de-documento', licenca: 'documento_de_pessoa.ler' },
  aptidoes: { rota: '/pessoas/aptidoes', licenca: 'documento_de_pessoa.ler' },
  exigencias: { rota: '/pessoas/exigencias', licenca: 'documento_de_pessoa.ler' },
  funcoes: { rota: '/pessoas/funcoes', licenca: 'documento_de_pessoa.ler' },
  /** A lista mínima de pessoas para quem autoriza alguém a operar (item 7 dos robôs). */
  elegiveis: { rota: '/pessoas/aptidoes/elegiveis', licenca: 'aptidao.gravar' },
} as const;

export type ConsultaDePessoas = keyof typeof CONSULTAS_DE_PESSOAS;

export type OQueLe = Readonly<Record<ConsultaDePessoas, boolean>> & {
  /** A tela abre quando o perfil lê ao menos o impedimento ou os documentos. */
  readonly pagina: boolean;
};

/**
 * O que o perfil lê nesta tela. `null` é a sessão em trânsito: nada sai até
 * ela chegar — a consulta que dispara antes da sessão é a que volta 403.
 */
export function oQueLe(permissoes: readonly string[] | null): OQueLe {
  const tem = new Set(permissoes ?? []);
  const le = Object.fromEntries(
    (Object.keys(CONSULTAS_DE_PESSOAS) as ConsultaDePessoas[]).map((c) => [c, tem.has(CONSULTAS_DE_PESSOAS[c].licenca)]),
  ) as Record<ConsultaDePessoas, boolean>;
  return { ...le, pagina: permissoes !== null && abreAPaginaDePessoas(permissoes) };
}

/**
 * Quem abre a página: quem lê o impedimento OU os documentos — a mesma regra
 * de `LEITURA_DA_TELA['/pessoas']`, que recorta o menu (o teste
 * `src/componentes/menu-segue-a-tela.test.ts` confere as duas para todo papel).
 *
 * Escrita com as duas licenças por extenso, e não pela tabela acima, de
 * propósito: o portão da página só pode alcançar licença de LER — a análise do
 * H54 (`scripts/lib/acoes-e-licencas.mjs`) lê a condição e tomaria a tela por
 * uma tela-ação se ela alcançasse a licença de autorizar (`aptidao.gravar`).
 */
export function abreAPaginaDePessoas(permissoes: readonly string[]): boolean {
  return permissoes.includes('impedimento.ler') || permissoes.includes('documento_de_pessoa.ler');
}
